import { isNotLoginOrExpired } from '../../../../component/helpers/Common';
import { ChanelConfig } from '../../../../configs/GlobalConfig';
import authProvider from '../../../AuthenticationProvider';
import messageHub from '../../../signalr/SignalrMessageHub';
import store from '../../../../component/app/store';
import { REDUCER_NAME as COMMON_REDUCER_NAME } from '../../../../component/app/commonReducer';
import masterService from '../../Common/MasterService';
import {
  EXCHANGES,
  MAP_EXCHANGES,
  MAX_ITEM_LENGTH,
  PRICE_SCALE,
  supportedResolutions,
  SYMBOL_TYPES,
  config,
  guestResolutions,
} from './Const';
import historyProvider, { getPrice, isIndicator } from './historyProvider';

let subscribers = {};

const formatStockSymbol = (item, exchange, type = SYMBOL_TYPES.STOCK) => ({
  exchange,
  type,
  symbol: item.ticker,
  full_name: item.ticker,
  description: item.organShortName,
});

const formatFuturesSymbol = (item, exchange, type = SYMBOL_TYPES.FUTURES) => ({
  exchange,
  type,
  symbol: item.derivativeCode,
  full_name: item.derivativeCode,
  description: item.derivativeName,
});

const formatIndicesSymbol = (item, exchange, type = SYMBOL_TYPES.INDICES) => ({
  exchange,
  type,
  symbol: item.comGroupCode,
  full_name: item.comGroupCode,
  description: item.comGroupName,
});

const formatEconomySymbol = (item, exchange, type = SYMBOL_TYPES.ECONOMY) => ({
  exchange,
  type,
  symbol: item.code,
  full_name: item.code,
  description: item.name,
});

const formatCoveredWarrantSymbol = (
  item,
  exchange,
  type = SYMBOL_TYPES.COVERED_WARRANT,
) => ({
  exchange,
  type,
  symbol: item.coveredWarrantCode,
  full_name: item.coveredWarrantCode,
  description: item.coveredWarrantName,
});

const formatList = ({
  items,
  formatItem,
  userInput,
  exchange,
  searchFields,
}) => {
  if (!Array.isArray(items)) {
    return [];
  }
  const prioritizeResult = [];
  const result = [];
  const [firstField, ...rest] = searchFields;
  items.forEach(item => {
    const hasExchange =
      exchange === EXCHANGES.ALL ||
      (MAP_EXCHANGES[exchange] &&
        MAP_EXCHANGES[exchange].includes(item.comGroupCode));
    const hasPrioritizeField =
      item[firstField] &&
      item[firstField].toLowerCase().includes(userInput.toLowerCase());

    if (hasPrioritizeField && hasExchange) {
      prioritizeResult.push(formatItem(item, exchange));
      return;
    }
    const includeChar = rest.some(
      field =>
        item[field] &&
        item[field].toLowerCase().includes(userInput.toLowerCase()),
    );
    if (includeChar && hasExchange) {
      return result.push(formatItem(item, exchange));
    }
  });

  return prioritizeResult.concat(result);
};

const getALlSymbols = (userInput, exchange) => {
  const promises = [
    getStockSymbols(userInput, exchange),
    getFuturesSymbols(userInput, exchange),
    getIndicesSymbols(userInput, exchange),
    getEconomySymbols(userInput, exchange),
    getCoveredWarrantSymbols(userInput, exchange),
  ];

  return Promise.all(promises).then(
    ([stocks, futures, indices, economies, coveredWarrants]) => [
      ...stocks,
      ...futures,
      ...indices,
      ...economies,
      ...coveredWarrants,
    ],
  );
};

const getStockSymbols = (userInput, exchange) => {
  const searchFields = ['ticker', 'organShortName'];
  const stocks = store.getState()[COMMON_REDUCER_NAME].listOrganizationSearch;
  return Promise.resolve(
    formatList({
      items: stocks,
      searchFields,
      userInput,
      exchange,
      formatItem: formatStockSymbol,
    }),
  );
};

const getFuturesSymbols = (userInput, exchange) => {
  const searchFields = ['comGroupCode', 'derivativeName'];
  return new Promise(resolve => {
    masterService.getAllDerivativesForChart().then(({ items }) => {
      resolve(
        formatList({
          items,
          searchFields,
          userInput,
          exchange,
          formatItem: formatFuturesSymbol,
        }),
      );
    });
  });
};

const getIndicesSymbols = (userInput, exchange) => {
  const searchFields = ['comGroupCode', 'comGroupName'];
  const items = store.getState()[COMMON_REDUCER_NAME].listCompanyGroup;
  return Promise.resolve(
    formatList({
      items,
      userInput,
      exchange,
      searchFields,
      formatItem: formatIndicesSymbol,
    }),
  );
};

const getEconomySymbols = (userInput, exchange) => {
  const searchFields = ['code', 'name'];
  const items = store.getState()[COMMON_REDUCER_NAME].listEconomy;
  return Promise.resolve(
    formatList({
      items,
      userInput,
      exchange,
      searchFields,
      formatItem: formatEconomySymbol,
    }),
  );
};

const getCoveredWarrantSymbols = (userInput, exchange) => {
  const searchFields = ['coveredWarrantCode', 'coveredWarrantName'];
  const items = store.getState()[COMMON_REDUCER_NAME].listCoveredWarrant;
  return Promise.resolve(
    formatList({
      items,
      userInput,
      exchange,
      searchFields,
      formatItem: formatCoveredWarrantSymbol,
    }),
  );
};

const isCreateNewBar = (resolution, ticker, symbolInfo, subscriberUID) => {
  const tradingDate = new Date(ticker.tradingDate);
  let minutes = resolution;
  if (resolution.includes('D')) {
    minutes = 1440;
  }
  if (resolution.includes('W')) {
    minutes = 10080;
  }

  return (
    subscribers[subscriberUID] &&
    tradingDate.getTime() >
      subscribers[subscriberUID].lastBar.time + minutes * 60000
  );
};

const isIntraday = resolution => {
  return !resolution.includes('D') || !resolution.includes('W');
};

export const getDerivativeChannel = symbol => {
  return `${ChanelConfig.TickChanel}.${symbol.derivativeCode}`;
};

const onReceiveTickers = (
  tickers,
  symbolInfo,
  resolution,
  onRealtimeCallback,
  onResetCacheNeededCallback,
  subscriberUID,
) => {
  const ticker = tickers.find(t => t.ticker === symbolInfo.ticker);
  if (!ticker || !subscribers[subscriberUID]) {
    return;
  }
  if (isCreateNewBar(resolution, ticker, symbolInfo, subscriberUID)) {
    subscribers[subscriberUID].lastBar = {
      time: new Date(ticker.tradingDate).getTime(),
      low: getPrice(symbolInfo, ticker.matchPrice),
      high: getPrice(symbolInfo, ticker.matchPrice),
      open: getPrice(symbolInfo, ticker.matchPrice),
      close: getPrice(symbolInfo, ticker.matchPrice),
      volume: ticker.matchVolume,
    };
  } else {
    const volume = isIntraday(resolution)
      ? subscribers[subscriberUID].lastBar.volume + ticker.matchVolume
      : ticker.totalMatchVolume;
    subscribers[subscriberUID].lastBar = {
      ...subscribers[subscriberUID].lastBar,
      volume,
      low: Math.min(
        getPrice(symbolInfo, ticker.matchPrice),
        subscribers[subscriberUID].lastBar.low,
      ),
      high: Math.max(
        getPrice(symbolInfo, ticker.matchPrice),
        subscribers[subscriberUID].lastBar.high,
      ),
      close: getPrice(symbolInfo, ticker.matchPrice),
    };
  }
  onRealtimeCallback(subscribers[subscriberUID].lastBar);
};

const onReceiveIndex = (
  data,
  symbolInfo,
  resolution,
  onRealtimeCallback,
  onResetCacheNeededCallback,
  subscriberUID,
) => {
  const index = data.find(
    item =>
      item.comGroupCode &&
      symbolInfo.ticker &&
      item.comGroupCode.toLowerCase() === symbolInfo.ticker.toLowerCase(),
  );

  if (!index || !subscribers[subscriberUID]) {
    return;
  }

  if (isCreateNewBar(resolution, index, symbolInfo, subscriberUID)) {
    subscribers[subscriberUID].lastBar = {
      time: new Date(index.tradingDate).getTime(),
      low: index.indexValue,
      high: index.indexValue,
      open: index.indexValue,
      close: index.indexValue,
      volume: index.matchVolume,
    };
  } else {
    const volume = isIntraday(resolution)
      ? subscribers[subscriberUID].lastBar.volume + index.matchVolume
      : index.totalMatchVolume;
    subscribers[subscriberUID].lastBar = {
      ...subscribers[subscriberUID].lastBar,
      volume,
      low: Math.min(index.indexValue, subscribers[subscriberUID].lastBar.low),
      high: Math.max(
        getPrice(symbolInfo, index.indexValue),
        subscribers[subscriberUID].lastBar.high,
      ),
      close: getPrice(symbolInfo, index.indexValue),
    };
  }
  onRealtimeCallback(subscribers[subscriberUID].lastBar);
};

const onReceiveDerivative = (
  data,
  symbolInfo,
  resolution,
  onRealtimeCallback,
  onResetCacheNeededCallback,
  subscriberUID,
) => {
  const derivative = data.find(
    item => item.derivativeCode === symbolInfo.derivativeCode,
  );
  if (!derivative || !subscribers[subscriberUID]) {
    return;
  }
  if (isCreateNewBar(resolution, derivative, symbolInfo, subscriberUID)) {
    subscribers[subscriberUID].lastBar = {
      time: new Date(derivative.tradingDate).getTime(),
      low: derivative.matchPrice,
      high: derivative.matchPrice,
      open: derivative.matchPrice,
      close: derivative.matchPrice,
      volume: derivative.matchPrice,
    };
  } else {
    const volume = isIntraday(resolution)
      ? subscribers[subscriberUID].lastBar.volume + derivative.matchVolume
      : derivative.totalMatchVolume;
    subscribers[subscriberUID].lastBar = {
      ...subscribers[subscriberUID].lastBar,
      volume,
      low: Math.min(
        derivative.matchPrice,
        subscribers[subscriberUID].lastBar.low,
      ),
      high: Math.max(
        getPrice(symbolInfo, derivative.matchPrice),
        subscribers[subscriberUID].lastBar.high,
      ),
      close: getPrice(symbolInfo, derivative.matchPrice),
    };
  }
  onRealtimeCallback(subscribers[subscriberUID].lastBar);
};

const getRealSymbol = symbolName => {
  const rootState = store.getState();
  const stocks = rootState[COMMON_REDUCER_NAME].listOrganizationByTicker;
  const indices = rootState[COMMON_REDUCER_NAME].listCompanyGroupByComGroupCode;
  const economies = rootState[COMMON_REDUCER_NAME].listEconomyByEconomyCode;
  const coveredWarrants =
    rootState[COMMON_REDUCER_NAME].coveredWarrantDataByCode;

  if (coveredWarrants[symbolName]) {
    return {
      code: coveredWarrants[symbolName].coveredWarrantCode,
      type: SYMBOL_TYPES.COVERED_WARRANT,
    };
  }

  if (stocks[symbolName]) {
    return {
      type: SYMBOL_TYPES.STOCK,
      code: stocks[symbolName].organCode,
    };
  }

  if (indices[symbolName]) {
    return {
      type: SYMBOL_TYPES.INDICES,
      code: indices[symbolName].comGroupCode,
    };
  }

  if (economies[symbolName]) {
    return {
      type: SYMBOL_TYPES.ECONOMY,
      code: economies[symbolName].code,
    };
  }
  const derivatives = store.getState()[COMMON_REDUCER_NAME].listDerivative;
  const derivative = derivatives.find(item => item.organCode === symbolName);

  return {
    type: SYMBOL_TYPES.FUTURES,
    derivativeCode: derivative ? derivative.derivativeCode : symbolName,
  };
};

export default {
  onReady: cb => {
    setTimeout(async () => {
      const user = await authProvider.getUser();
      cb({
        ...config,
        supported_resolutions: isNotLoginOrExpired(user)
          ? guestResolutions
          : supportedResolutions,
      });
    }, 0);
  },
  searchSymbols: (userInput, exchange, symbolType, onResultReadyCallback) => {
    const mapFunction = {
      [SYMBOL_TYPES.ALL]: getALlSymbols,
      [SYMBOL_TYPES.STOCK]: getStockSymbols,
      [SYMBOL_TYPES.FUTURES]: getFuturesSymbols,
      [SYMBOL_TYPES.INDICES]: getIndicesSymbols,
      [SYMBOL_TYPES.ECONOMY]: getEconomySymbols,
      [SYMBOL_TYPES.COVERED_WARRANT]: getCoveredWarrantSymbols,
    };
    if (
      mapFunction[symbolType] &&
      typeof mapFunction[symbolType] === 'function'
    ) {
      mapFunction[symbolType](userInput, exchange).then(symbols => {
        symbols.length = Math.min(MAX_ITEM_LENGTH, symbols.length);
        onResultReadyCallback(symbols);
      });
    }
  },
  resolveSymbol: (
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback,
  ) => {
    const { type, code, derivativeCode } = getRealSymbol(symbolName);
    const symbolStub = {
      type,
      minmov: 1,
      derivativeCode,
      displayCode: symbolName || symbolName,
      code: code || symbolName,
      name: symbolName,
      ticker: symbolName,
      pricescale: PRICE_SCALE,
      supported_resolution: supportedResolutions,
      has_intraday: type !== SYMBOL_TYPES.ECONOMY,
      session: '0900-1500',
      timezone: 'Asia/Bangkok',
      intraday_multipliers: ['1', '60'],
    };

    setTimeout(() => {
      onSymbolResolvedCallback(symbolStub);
    }, 0);
  },

  getBars: (
    symbolInfo,
    resolution,
    from,
    to,
    onHistoryCallback,
    onErrorCallback,
    firstDataRequest,
  ) => {
    historyProvider
      .getBars(symbolInfo, resolution, from, to, firstDataRequest)
      .then(bars => {
        if (bars === null) {
          onHistoryCallback([], { noData: true });
          return;
        }
        if (firstDataRequest && bars.length && !isIndicator(symbolInfo)) {
          const subscriberUID = `${symbolInfo.ticker}_${resolution}`;
          subscribers[subscriberUID] = {
            lastBar: bars[bars.length - 1],
          };
        }
        onHistoryCallback(bars, { noData: false });
      })
      .catch(err => {
        onErrorCallback(err);
      });
  },
  subscribeBars: (
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscriberUID,
    onResetCacheNeededCallback,
  ) => {
    if (!subscribers[subscriberUID]) {
      return;
    }
    subscribers[subscriberUID].symbolInfo = symbolInfo;
    if (symbolInfo.type === SYMBOL_TYPES.STOCK) {
      subscribers[subscriberUID].listener = tickers =>
        onReceiveTickers(
          tickers,
          symbolInfo,
          resolution,
          onRealtimeCallback,
          onResetCacheNeededCallback,
          subscriberUID,
        );
      messageHub.subscribe(
        ChanelConfig.TickChanel,
        subscribers[subscriberUID].listener,
      );
    }

    if (symbolInfo.type === SYMBOL_TYPES.INDICES) {
      subscribers[subscriberUID].listener = data =>
        onReceiveIndex(
          data,
          symbolInfo,
          resolution,
          onRealtimeCallback,
          onResetCacheNeededCallback,
          subscriberUID,
        );
      messageHub.subscribe(
        ChanelConfig.IndexChannel,
        subscribers[subscriberUID].listener,
      );
    }

    if (symbolInfo.type === SYMBOL_TYPES.FUTURES) {
      subscribers[subscriberUID].triggerListener = () => {};
      subscribers[subscriberUID].listener = data =>
        onReceiveDerivative(
          data,
          symbolInfo,
          resolution,
          onRealtimeCallback,
          onResetCacheNeededCallback,
          subscriberUID,
        );
      messageHub.subscribe(
        ChanelConfig.DerivativeChannel,
        subscribers[subscriberUID].listener,
      );
      messageHub.subscribe(
        getDerivativeChannel(symbolInfo),
        subscribers[subscriberUID].triggerListener,
      );
    }
  },
  unsubscribeBars: subscriberUID => {
    if (!subscribers[subscriberUID]) {
      return;
    }
    if (subscribers[subscriberUID].symbolInfo.type === SYMBOL_TYPES.STOCK) {
      messageHub.unsubscribe(
        ChanelConfig.TickChanel,
        subscribers[subscriberUID].listener,
      );
    }

    if (subscribers[subscriberUID].symbolInfo.type === SYMBOL_TYPES.INDICES) {
      messageHub.unsubscribe(
        ChanelConfig.IndexChannel,
        subscribers[subscriberUID].listener,
      );
    }

    if (subscribers[subscriberUID].symbolInfo.type === SYMBOL_TYPES.FUTURES) {
      messageHub.unsubscribe(
        ChanelConfig.DerivativeChannel,
        subscribers[subscriberUID].listener,
      );
      messageHub.unsubscribe(
        getDerivativeChannel(subscribers[subscriberUID].symbolInfo),
        subscribers[subscriberUID].triggerListener,
      );
    }

    const { [subscriberUID]: _, ...rest } = subscribers;
    subscribers = rest;
  },
  getMarks: async (
    symbolInfo,
    startDate,
    endDate,
    onDataCallback,
    resolution,
  ) => {
    if (symbolInfo.type !== SYMBOL_TYPES.STOCK) {
      return;
    }
    const marks = await historyProvider.getMarks(
      symbolInfo,
      startDate,
      endDate,
    );
    onDataCallback(marks);
  },
  getTimescaleMarks: async (
    symbolInfo,
    startDate,
    endDate,
    onDataCallback,
    resolution,
  ) => {
    if (symbolInfo.type !== SYMBOL_TYPES.STOCK) {
      return;
    }
    const timescaleMarks = await historyProvider.getTimescaleMarks(
      symbolInfo,
      startDate,
      endDate,
    );
    onDataCallback(timescaleMarks);
  },
  calculateHistoryDepth: (resolution, resolutionBack, intervalBack) => {},
  getServerTime: cb => {},
};
